/**
 * UI locale → operating-region cue.
 *
 * A new owner arrives from the external marketing site carrying a `startProperties`
 * hand-off whose `locale` picks the UI language (see
 * `pages/configurator-v3/lib/startProperties.ts`). That same language is a strong hint
 * for WHERE the owner operates, so we use it to PRE-SELECT the country / operating
 * region in the configurator and onboarding — which in turn drives the currency + prices
 * mix (configurator) and the deploy location (onboarding).
 *
 * It is only a STARTING cue: a region the owner explicitly picked, or one already saved
 * on the session, always wins (the consumers apply this map only when there is no saved
 * country). A locale with no regional home returns `null`, so the caller keeps its own
 * default (International / USD).
 *
 * The tenant now ships several UI languages (`en`; `mx`/`cl`/`co`/`arg`/`pe`/`ve` for the
 * Spanish markets; `bra` Portuguese; `za` Afrikaans; `cfr`/`cen` Canadian French/English) via the
 * `languages` list in the deploy config, each cued to an operating region below. Backend
 * enablement of those regions and their pricing is tracked separately. Note the
 * operating-region catalogue does NOT itself
 * encode this link usefully (the North-America group that holds `MEX` is tagged
 * `language: "en"`), so the mapping is curated here against the app's own locale codes.
 * Add a row as new language/region pairs launch.
 */

import { readStartMarketHandoff } from '@/utils/start-properties-market';

/** App UI locale code → operating-region code. Keyed on the app's own locale ids
 *  (`window.config.languages`), not BCP-47. e.g. Mexican Spanish → Mexico, Canadian
 *  French → Canada. Region codes are cues only; backend enablement is separate.
 *  Brazil/Argentina use the Intl-safe app ids `bra`/`arg` (their backend codes `br`/`ar` are
 *  read by CLDR as Breton/Arabic and would break pluralization + the i18n gate); langCodeMap
 *  bridges them to `br`/`ar` at the API boundary. */
const LOCALE_REGION: Readonly<Record<string, string>> = {
  mx: 'MEX',
  cl: 'CHL',
  co: 'COL',
  pe: 'PER',
  ve: 'VEN',
  za: 'ZAF',
  bra: 'BRA',
  arg: 'ARG',
  cfr: 'CAN',
  cen: 'CAN',
};

/**
 * The operating-region code cued by a UI locale, or `null` when the locale has no
 * regional home (the caller then keeps its default region). Tolerant of casing and of a
 * BCP-47-style tag (`es-MX` → tries `es-mx` then the `es` base) even though the app's own
 * locale codes are plain (`en` / `mx`).
 */
export function regionForLocale(locale: string | null | undefined): string | null {
  if (!locale) return null;
  const full = locale.trim().toLowerCase();
  if (!full) return null;
  const base = full.split(/[-_]/)[0];
  return LOCALE_REGION[full] ?? LOCALE_REGION[base] ?? null;
}

/**
 * sessionStorage key recording the locale a `startProperties` hand-off applied this
 * session. Set by the boot shim (`src/dev.js`) and `useStartProperties`, and deliberately
 * never cleared mid-session — so it is the durable signal for "the language the marketing
 * site handed off", surviving the raw-param stash being consumed. The literal MIRRORS
 * `START_LOCALE_OVERRIDE_KEY` in `pages/configurator-v3/lib/startProperties.ts` and
 * `src/dev.js` (kept in lockstep; plain-JS `dev.js` can't import it).
 */
const HANDOFF_LOCALE_KEY = 'ows-start-locale-override';

/** The UI locale the `startProperties` hand-off applied this session, or `null`. */
export function readHandoffLocale(): string | null {
  try {
    return sessionStorage.getItem(HANDOFF_LOCALE_KEY);
  } catch {
    return null;
  }
}

/**
 * The operating-region code cued by THIS session's hand-off locale, or `null`. The
 * primary cue the configurator/onboarding pre-select from — reads the durable hand-off
 * locale, so it holds even after the raw `startProperties` param has been consumed.
 */
export const handoffRegion = (): string | null => regionForLocale(readHandoffLocale());

/**
 * The operating region to PRE-SELECT in the configurator, preferring the EXPLICIT `or` from the
 * marketing-site `startProperties` hand-off (`INT` | `MEX` — authoritative) and falling back to
 * the region merely CUED by the UI locale ({@link handoffRegion}) when no explicit `or` was
 * sent. Both yield the catalogue's own region codes, so callers still validate the result
 * against the selectable regions (present, not "coming soon") before applying it — and a country
 * already saved on the session always wins over this cue.
 */
export const preferredHandoffRegion = (): string | null => readStartMarketHandoff().or ?? handoffRegion();
